import React, { useContext } from 'react';
import * as yup from 'yup';
import { InputText } from 'primereact/inputtext';
import useTranslationLgs from '@hooks/i18n/useTranslation.tsx';
import { EntityTypeEnum } from '@api/ringil-api.v2.tsx';
import { ApiContext } from '@api/api.tsx';
import useForm from '@hooks/useForm/useForm.tsx';
import { VerticalFieldAndValue } from '@components/ringil3/Containers/VerticalFieldAndValue.tsx';
import SvgNewTagCheck from '@components/ringil3/icons/tags/NewTagCheck.tsx';
import RingilButton from '@components/ringil3/Buttons/RingilButton.tsx';

interface ComponentProps {
  /** The kind of entity the label is for — labels are kept per entity type. */
  entityType: EntityTypeEnum;
  /** The label was saved; the endpoint returns no id, so the caller reloads its list and finds it by name. */
  onCreated: (labelValue: string) => void;
  /** Shows a Cancel button next to the submit one. */
  onCancel?: () => void;
}

interface LabelFormValues {
  color: string;
  name: string;
}

/**
 * The name + colour form for creating a company label, through the standard label endpoint. Shared
 * by the labels tag on entities and the AI dispatcher's label pickers, so a label looks the same
 * whichever place it was made in. The keys stay under `LabelsTag.*`, where the form came from.
 */
const RingilLabelForm: React.FC<ComponentProps> = ({ entityType, onCreated, onCancel }) => {
  const { tr } = useTranslationLgs();
  const { createNewLabelForCompany } = useContext(ApiContext);

  // Label colours are stored on the label and shown as its background everywhere, so they are data, not theme tokens.
  const labelColors = [
    { colorName: tr('LabelsTag.green', 'green'), hexValue: '#60BD4E' },
    { colorName: tr('LabelsTag.yellow', 'yellow'), hexValue: '#F1D600' },
    { colorName: tr('LabelsTag.orange', 'orange'), hexValue: '#FF9E19' },
    { colorName: tr('LabelsTag.red', 'red'), hexValue: '#EB5A46' },
    { colorName: tr('LabelsTag.magenta', 'magenta'), hexValue: '#C277E0' },
    { colorName: tr('LabelsTag.blue', 'blue'), hexValue: '#0179BF' },
    { colorName: tr('LabelsTag.lightblue', 'lightblue'), hexValue: '#00C2DF' },
    { colorName: tr('LabelsTag.green', 'green'), hexValue: '#51E898' },
    { colorName: tr('LabelsTag.darkblue', 'darkblue'), hexValue: '#334563' },
    { colorName: tr('LabelsTag.pink', 'pink'), hexValue: '#F62DAE' },
    { colorName: tr('LabelsTag.black', 'black'), hexValue: '#000000' },
    { colorName: tr('LabelsTag.cherry', 'cherry'), hexValue: '#A63A50' },
    { colorName: tr('LabelsTag.indigo', 'indigo'), hexValue: '#470063' },
    { colorName: tr('LabelsTag.sage', 'sage'), hexValue: '#91C499' },
    { colorName: tr('LabelsTag.silver', 'silver'), hexValue: '#AFBFC0' },
    { colorName: tr('LabelsTag.lemon', 'lemon'), hexValue: '#CFD11A' },
    { colorName: tr('LabelsTag.sandy', 'sandy'), hexValue: '#E0B773' },
  ];

  const form = useForm<LabelFormValues>(
    yup.object().shape({
      color: yup.string().required(),
      name: yup.string().required(),
    }),
    { color: '', name: '' },
    values =>
      createNewLabelForCompany({ labelValue: values.name, labelColor: values.color, entityType }, () => {
        form.overwriteValues({ color: '', name: '' });
        onCreated(values.name);
      }),
    false,
    false,
    null,
    'scroll',
  );

  return (
    <>
      <VerticalFieldAndValue label={tr(`LabelsTag.newLabel`, `New label`)} labelMode={'normal'} errorMessage={form.errors.name}>
        <InputText value={form.values.name} onChange={e => form.setFieldValue(form.names.name, e.target.value)} />
      </VerticalFieldAndValue>

      <VerticalFieldAndValue label={tr(`LabelsTag.tagColor`, `Tag color`)} labelMode={'normal'}>
        <div className="flex flex-wrap gap-2">
          {labelColors.map((color, index) => (
            <div
              key={index}
              className="my-1 flex h-8 cursor-pointer items-center justify-center whitespace-nowrap rounded px-2 text-white"
              style={{ backgroundColor: color.hexValue }}
              onClick={() => form.setFieldValue(form.names.color, color.hexValue)}
            >
              <div>{color.hexValue === form.values.color && <SvgNewTagCheck />}</div>
              <div>{color.colorName}</div>
            </div>
          ))}
        </div>
      </VerticalFieldAndValue>
      <div className="flex gap-2">
        <RingilButton label={tr(`LabelsTag.addLabelButton`, `Add label`)} onClick={() => form.validateAndSend()} />
        {onCancel && <RingilButton mode={'text-only'} label={tr('generic.cancel', 'Cancel')} onClick={onCancel} />}
      </div>
    </>
  );
};

export default RingilLabelForm;
